/**
 * /well-testing/jobs/:id/readings/import — WT-3b (fieldiq-api#1252): import the
 * shift's data-logger file into a job's readings.
 *
 *   POST /jobs/:id/readings/import/parse                the file → proposed rows. SAVES NOTHING.
 *   POST /jobs/:id/readings/import/confirm              the rows the reviewer kept, with agent_run_id
 *   POST /jobs/:id/readings/import/runs/:runId/dismiss  abandon the parse
 *
 * Copied from the Pipeline & Facility anomaly import (AnomalyAiRead), not shared
 * with it: upload → review every proposed row → confirm the kept rows → a result
 * naming what was imported and every row skipped.
 *
 * THE ROWS ARE AN AI DRAFT. The AI only maps the columns; the values are copied
 * from the file by code. Each row shows its source line, its read_at in the
 * tenant's zone, and every value AS THE FILE WROTE IT with its unit — nothing
 * here converts or computes. The column map says where each unit came from
 * (the header, or the tenant's default because the file states none).
 *
 * Only a `ready` row can be kept. A refused, duplicate or repeated row says why
 * in words and its box is disabled; it is never sent.
 *
 * OFFICE ONLY (WT_DISPATCH, mirroring fieldiq-api lib/wt-roles.ts). A field
 * hand who reaches the URL is told so instead of being offered an upload the API
 * would 403. Every refusal reads as a sentence, never as success or as empty.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileSpreadsheet, Sparkles, Upload } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirmWtLoggerImport, useDismissWtLoggerImport, useParseWtLoggerFile, useWtJob } from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchWt, wtJobLoggable, wtUnitLabel } from '../../lib/well-testing';
import {
  WT_IMPORT_SKIP_LABEL,
  wtImportReadAt,
  wtImportRefusal,
  wtImportResultSentence,
  wtImportRowKeepable,
  wtImportStatusSkin,
  wtImportUnitSource,
  wtImportValue,
  wtImportValueColumns,
} from '../../lib/wt-logger-import';
import type { WtImportParseResult, WtImportResult, WtMeta } from '../../types/well-testing';
import { useBackToList } from '../downhole/recordPage';
import { isNotFound, WT_EYEBROW, WT_PRIMARY, WT_SECONDARY, WtBadge, WtEmpty, WtField, WtFormError, WtLoading, WtPanel } from './WtBits';

const TH = 'px-2 py-2';
const HEAD_ROW = 'border-b border-fieldiq-border text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';

export default function WtLoggerImportScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canImport = canDispatchWt(user?.role);
  const back = useBackToList(id ? `/well-testing/jobs/${id}?tab=readings` : '/well-testing/jobs');
  const jobQuery = useWtJob(id);
  const job = jobQuery.data?.data ?? null;
  const parse = useParseWtLoggerFile();
  const confirm = useConfirmWtLoggerImport();
  const dismiss = useDismissWtLoggerImport();

  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [parsed, setParsed] = useState<{ data: WtImportParseResult; meta: WtMeta } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ result: WtImportResult; refs: (string | null)[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tz = parsed?.meta.timeZone ?? jobQuery.data?.meta.timeZone ?? null;
  const failure = queryLoadFailure(jobQuery);
  const notFound = isNotFound(queryFailureError(jobQuery));

  const startOver = () => {
    setParsed(null);
    setSelected(new Set());
    setFile(null);
    setInputKey((k) => k + 1);
  };

  const read = () => {
    if (!file || !id) return;
    setError(null);
    setNotice(null);
    setResult(null);
    parse.mutate(
      { jobId: id, file },
      {
        onSuccess: (r) => {
          setParsed(r);
          setSelected(new Set(r.data.rows.filter((x) => wtImportRowKeepable(x.status)).map((x) => x.index)));
        },
        onError: (err) => setError(wtImportRefusal(err, 'The file could not be read. Nothing was saved.')),
      },
    );
  };

  const rows = parsed?.data.rows ?? [];
  const keepable = rows.filter((r) => wtImportRowKeepable(r.status));
  const kept = keepable.filter((r) => selected.has(r.index));
  const allKept = keepable.length > 0 && kept.length === keepable.length;

  const toggle = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const toggleAll = () => setSelected(allKept ? new Set() : new Set(keepable.map((r) => r.index)));

  const save = () => {
    if (!parsed || !id || !kept.length) return;
    setError(null);
    const refs = kept.map((r) => r.sourceRef);
    confirm.mutate(
      { jobId: id, body: { rows: kept.map((r) => r.row), agent_run_id: parsed.data.agentRunId } },
      {
        onSuccess: (res) => {
          startOver();
          setResult({ result: res, refs });
        },
        onError: (err) => setError(wtImportRefusal(err, 'The rows could not be saved. Nothing was saved.', refs)),
      },
    );
  };

  const discard = () => {
    if (!parsed || !id) return;
    setError(null);
    dismiss.mutate(
      { jobId: id, runId: parsed.data.agentRunId },
      {
        onSuccess: () => {
          startOver();
          setNotice('Dismissed. Nothing from that file was saved.');
        },
        onError: (err) => setError(wtImportRefusal(err, 'The parse could not be dismissed.')),
      },
    );
  };

  const valueColumns = parsed ? wtImportValueColumns(parsed.data.columnMap) : [];
  const hasNotes = !!parsed?.data.columnMap.some((c) => c.field === 'notes');
  const busy = confirm.isPending || dismiss.isPending;

  return (
    <div data-testid="wt-logger-import-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job?.jobNumber ? `${WT_EYEBROW} · ${job.jobNumber}` : WT_EYEBROW}
        title="Import logger file"
        description={job ? `${job.customerName ?? '—'} · ${job.wellName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canImport ? (
            <WtPanel eyebrow="Readings" title="Import logger file" testId="wt-import-office-only">
              <WtEmpty>Only the office can import a logger file. Enter readings one by one on the job&apos;s Readings tab.</WtEmpty>
            </WtPanel>
          ) : jobQuery.isLoading ? (
            <WtLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <WtPanel eyebrow="Readings" title="Import logger file">
              <WtEmpty>This job was not found. It may have been deleted.</WtEmpty>
            </WtPanel>
          ) : failure ? (
            <WtPanel eyebrow="Readings" title="Import logger file">
              <LoadFailureNotice error={queryFailureError(jobQuery)} kind={failure} subject="this job" />
            </WtPanel>
          ) : (
            <>
              {job && !wtJobLoggable(job.status) && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="wt-import-locked">
                  {job.status === 'draft'
                    ? 'This job is still a draft. Schedule it before importing readings.'
                    : 'This job is completed or cancelled, so it takes no more readings.'}
                </p>
              )}

              {!parsed && (
                <WtPanel eyebrow="Step 1" title="Choose the logger file" testId="wt-import-upload">
                  <div className="space-y-3 px-4 py-4">
                    <p className="text-sm text-fieldiq-text">
                      The shift&apos;s data-logger export (CSV, XLSX or XLS, up to 10 MB). The AI maps the columns; FieldIQ copies every value from
                      the file as written. You review every row before anything is saved.
                    </p>
                    <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
                      <WtField label="Logger file">
                        <input
                          key={inputKey}
                          type="file"
                          accept=".csv,.txt,.xlsx,.xls,text/csv"
                          aria-label="Logger file"
                          className="block w-full text-sm text-fieldiq-text file:mr-3 file:rounded-fi-control file:border file:border-fieldiq-border file:bg-fieldiq-card file:px-3 file:py-2 file:text-sm file:text-fieldiq-text"
                          disabled={parse.isPending}
                          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
                        />
                      </WtField>
                      <button
                        type="button"
                        className={WT_PRIMARY}
                        disabled={!file || parse.isPending || (!!job && !wtJobLoggable(job.status))}
                        onClick={read}
                        data-testid="wt-import-parse"
                      >
                        <Upload className="size-4" aria-hidden="true" />
                        {parse.isPending ? 'Reading…' : 'Read file'}
                      </button>
                    </div>
                    {parse.isPending && (
                      <p className="text-sm text-fieldiq-text" role="status" data-testid="wt-import-reading">
                        Reading {file?.name ?? 'the file'}… this can take a minute. Nothing is saved.
                      </p>
                    )}
                    <WtFormError message={error} />
                    {notice && (
                      <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text" role="status" data-testid="wt-import-dismissed">
                        {notice}
                      </p>
                    )}
                  </div>
                </WtPanel>
              )}

              {parsed && (
                <>
                  <WtPanel
                    eyebrow="Step 2 · AI draft"
                    title="Column map"
                    testId="wt-import-columns"
                    footer={`${parsed.data.fileName} · ${parsed.data.sourceLineCount} line${parsed.data.sourceLineCount === 1 ? '' : 's'} in the file`}
                  >
                    <div className="space-y-3 px-4 py-4">
                      <p
                        data-testid="wt-import-draft-label"
                        className="flex items-start gap-2 rounded-fi-control border border-fi-amber/60 border-l-4 border-l-fi-amber bg-fi-signal-amber/10 px-4 py-3 text-sm font-bold text-fieldiq-text"
                      >
                        <Sparkles className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                        AI draft of {parsed.data.fileName}. Check the map and every row against the file — nothing is saved until you confirm.
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm" data-testid="wt-import-column-map">
                          <thead>
                            <tr className={HEAD_ROW}>
                              <th className={TH}>Sheet</th>
                              <th className={TH}>Column</th>
                              <th className={TH}>Header in the file</th>
                              <th className={TH}>Imported as</th>
                              <th className={TH}>Unit</th>
                              <th className={TH}>Unit from</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parsed.data.columnMap.map((c) => (
                              <tr key={`${c.sheet}-${c.column}`} data-testid="wt-import-column" className="border-b border-fieldiq-border/60">
                                <td className="px-2 py-2 text-fieldiq-muted">{c.sheet || '—'}</td>
                                <td className="px-2 py-2 font-mono text-fieldiq-muted">{c.column}</td>
                                <td className="px-2 py-2 font-mono text-fieldiq-text">{c.header || '—'}</td>
                                <td className="px-2 py-2 text-fieldiq-text">{c.field ? c.label ?? c.field : <span className="text-fieldiq-muted">Not imported</span>}</td>
                                <td className="px-2 py-2 font-mono text-fieldiq-text">{c.unit ? wtUnitLabel(c.unit) : '—'}</td>
                                <td className="px-2 py-2 text-fieldiq-text" data-testid="wt-import-unit-source" data-source={c.unitSource ?? ''}>
                                  {wtImportUnitSource(c)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {parsed.data.warnings.length > 0 && (
                        <ul className="list-disc space-y-1 pl-5 text-xs text-fieldiq-text" data-testid="wt-import-warnings">
                          {parsed.data.warnings.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </WtPanel>

                  <WtPanel
                    eyebrow="Step 3 · Review"
                    title="Proposed readings"
                    testId="wt-import-review"
                    footer={`${parsed.data.counts.proposed} proposed · ${parsed.data.counts.ok} ready · ${parsed.data.counts.invalid} refused · ${
                      parsed.data.counts.duplicate + parsed.data.counts.duplicateInBatch} already there or repeated`}
                  >
                    <div className="space-y-3 px-4 py-4">
                      {rows.length === 0 ? (
                        <p className="text-sm text-fieldiq-text" data-testid="wt-import-no-rows">
                          No readings were found in this file. Nothing was saved. Check it is the logger export, or enter the readings by hand.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[900px] text-left text-sm" data-testid="wt-import-rows">
                            <thead>
                              <tr className={`${HEAD_ROW} border-l-4 border-l-transparent`}>
                                <th className={TH}>
                                  <input
                                    type="checkbox"
                                    aria-label="Keep every ready row"
                                    checked={allKept}
                                    disabled={!keepable.length || busy}
                                    onChange={toggleAll}
                                    data-testid="wt-import-keep-all"
                                  />
                                </th>
                                <th className={TH}>Status</th>
                                <th className={TH}>Source</th>
                                <th className={TH}>Read at{tz ? ` (${tz})` : ''}</th>
                                {valueColumns.map((c) => (
                                  <th key={c.key} className={`${TH} text-right`}>
                                    {c.label}
                                  </th>
                                ))}
                                {hasNotes && <th className={TH}>Notes</th>}
                                <th className={TH}>Check</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r) => {
                                const ok = wtImportRowKeepable(r.status);
                                const skin = wtImportStatusSkin(r.status);
                                return (
                                  <tr
                                    key={r.index}
                                    data-testid="wt-import-row"
                                    data-status={r.status}
                                    className={`border-b border-fieldiq-border/60 border-l-4 align-top ${skin.rail}`}
                                  >
                                    <td className="px-2 py-2">
                                      <input
                                        type="checkbox"
                                        aria-label={`Keep ${r.sourceRef ?? `row ${r.index + 1}`}`}
                                        checked={ok && selected.has(r.index)}
                                        disabled={!ok || busy}
                                        onChange={() => toggle(r.index)}
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <WtBadge skin={skin} value={r.status} />
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-fieldiq-text" title={r.sourceText ?? undefined}>
                                      {r.sourceRef || '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 font-extrabold text-fieldiq-text" data-testid="wt-import-read-at">
                                      {wtImportReadAt(r.row.read_at, tz)}
                                    </td>
                                    {valueColumns.map((c) => (
                                      <td key={c.key} className="whitespace-nowrap px-2 py-2 text-right font-mono text-fieldiq-text" data-testid={`wt-import-value-${c.key}`}>
                                        {wtImportValue(r.row, c.key)}
                                      </td>
                                    ))}
                                    {hasNotes && <td className="px-2 py-2 text-xs text-fieldiq-text">{typeof r.row.notes === 'string' && r.row.notes ? r.row.notes : '—'}</td>}
                                    <td className="min-w-[220px] px-2 py-2 text-xs text-fieldiq-text">
                                      {ok ? (
                                        <span className="text-fieldiq-muted">Ready to import</span>
                                      ) : (
                                        <p className="font-bold" data-testid="wt-import-reason">
                                          Will not be saved: {r.reason || skin.label}
                                        </p>
                                      )}
                                      {r.warnings.length > 0 && (
                                        <ul className="mt-1 list-disc space-y-1 pl-4 text-fieldiq-muted">
                                          {r.warnings.map((w) => (
                                            <li key={w}>{w}</li>
                                          ))}
                                        </ul>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {parsed.data.excluded.length > 0 && (
                        <div className="text-xs text-fieldiq-muted" data-testid="wt-import-excluded">
                          <p className="font-bold text-fieldiq-text">Lines not read as readings:</p>
                          <ul className="mt-1 space-y-1">
                            {parsed.data.excluded.map((x) => (
                              <li key={x.ref} className="font-mono">
                                {x.ref}: {x.text || '—'}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <WtFormError message={error} />
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {rows.length > 0 ? (
                          <>
                            <button type="button" className={WT_SECONDARY} onClick={discard} disabled={busy} data-testid="wt-import-dismiss">
                              {dismiss.isPending ? 'Dismissing…' : 'Dismiss'}
                            </button>
                            <button type="button" className={WT_PRIMARY} onClick={save} disabled={!kept.length || busy} data-testid="wt-import-confirm">
                              {confirm.isPending ? 'Saving…' : `Confirm ${kept.length} row${kept.length === 1 ? '' : 's'}`}
                            </button>
                          </>
                        ) : (
                          <button type="button" className={WT_SECONDARY} onClick={startOver}>
                            Choose another file
                          </button>
                        )}
                      </div>
                    </div>
                  </WtPanel>
                </>
              )}

              {result && (
                <WtPanel eyebrow="Result" title="Import result" testId="wt-import-result">
                  <div className="space-y-3 px-4 py-4">
                    {(() => {
                      const nothing = result.result.counts.inserted === 0 && result.result.counts.skipped > 0;
                      return (
                        <p
                          data-testid="wt-import-result-sentence"
                          data-outcome={nothing ? 'nothing-imported' : 'imported'}
                          className={`rounded-fi-control border px-4 py-3 text-base font-bold text-fieldiq-text ${
                            nothing ? 'border-fi-amber border-l-4 border-l-fi-amber bg-fi-signal-amber/10' : 'border-fi-green/50 bg-fi-signal-green/10'
                          }`}
                        >
                          {wtImportResultSentence(result.result.counts)}
                        </p>
                      );
                    })()}
                    {result.result.skipped.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm" data-testid="wt-import-skipped">
                          <thead>
                            <tr className={HEAD_ROW}>
                              <th className={TH}>Source</th>
                              <th className={TH}>Read at{tz ? ` (${tz})` : ''}</th>
                              <th className={TH}>Skipped because</th>
                              <th className={TH}>Detail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.result.skipped.map((s) => (
                              <tr key={`${s.index}-${s.reason}`} data-testid="wt-import-skip" className="border-b border-fieldiq-border/60">
                                <td className="px-2 py-2 font-mono text-xs text-fieldiq-muted">{result.refs[s.index] ?? '—'}</td>
                                <td className="whitespace-nowrap px-2 py-2 text-fieldiq-text">{wtImportReadAt(s.readAt, tz)}</td>
                                <td className="px-2 py-2 text-fieldiq-text">{WT_IMPORT_SKIP_LABEL[s.reason] ?? s.reason}</td>
                                <td className="px-2 py-2 text-xs text-fieldiq-text">{s.message || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="flex flex-wrap justify-end gap-2">
                      <button type="button" className={WT_SECONDARY} onClick={() => { setResult(null); startOver(); }}>
                        <FileSpreadsheet className="size-4" aria-hidden="true" />
                        Import another file
                      </button>
                      <button type="button" className={WT_PRIMARY} onClick={back} data-testid="wt-import-back-to-readings">
                        Back to readings
                      </button>
                    </div>
                  </div>
                </WtPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
